import windows from "./windows"
import settings from "./settings"
import { parseJSON } from "@/utils/json"
import { localStore } from "./store"
import logger from "./logger"
import createAttribution from "./api/event/attribution"
import { VIEW_PRODUCT } from "./event"
import { pageTagging } from "@/core/tagging"

interface Attribution {
  ref: string
  src?: string
  refSrc?: string
}

type SerializedAttribution = Attribution & {
  linkAttributions?: PerLinkAttributions
}

export type PerLinkAttributions = Record<string, Record<string, string>>

type StoredAttributions = Record<
  string,
  {
    attribution: Attribution
    ts: number
    referrer: string
  }
>

const storageKey = "nosto:attribution"
const dataRef = "data-nosto-ref"
const attributionDuration = 3 * 60 * 1000
let currentAttributionLocation: string
let currentAttributionValue: { attribution: Attribution }

function w() {
  return windows.site
}

function locationUrl() {
  return normalizeKey(w().location.href)
}

function canonicalUrl() {
  const link = w().document.querySelector<HTMLLinkElement>("link[rel='canonical']")
  return link ? normalizeKey(link.getAttribute("href") || "") : undefined
}

function normalizeKey(url: string) {
  if (!url || url.startsWith("id:")) {
    return url
  }
  try {
    const u = new URL(url, w().location.href)
    if (settings.parameterlessAttributionNoQueryCheck) {
      return u.pathname
    }
    return u.pathname + u.search
  } catch (e) {
    logger.warn("Failed to normalize URL", e)
    return url
  }
}

function read() {
  const json = localStore.get(storageKey)
  const parsed = json ? parseJSON<StoredAttributions>(json) : {}
  // cleanup stale attribution
  const now = Date.now()
  return Object.fromEntries(Object.entries(parsed).filter(([_, { ts }]) => now - ts <= attributionDuration))
}

export function putAttribution(url: string, attribution: Attribution | undefined) {
  const serialized = JSON.stringify({
    ...read(),
    [normalizeKey(url)]: attribution && {
      attribution,
      ts: Date.now(),
      referrer: locationUrl()
    }
  })
  // empty attribution should lead to removal of storage item
  if (serialized === "{}") {
    localStore.remove(storageKey)
  } else {
    try {
      localStore.set(storageKey, serialized)
    } catch (e) {
      logger.warn("Attribution storage failed", e)
      throw e
    }
  }
}

function productIdToKey<T extends string | undefined>(productId: T) {
  return (productId ? `id:${productId}` : productId) as T
}

function attributionByKey() {
  const data = read()
  const productId = pageTagging().products?.[0]?.product_id
  const keys = [locationUrl(), canonicalUrl(), productIdToKey(productId)].filter(Boolean)
  return keys.map(key => ({ key, attr: data[key] })).find(({ attr }) => !!attr)
}

export function readAttribution(skipReferrer: boolean = false) {
  let read = false
  const { key, attr } = attributionByKey() || {}
  const referrer = normalizeKey(w().document.referrer)
  if (attr && (skipReferrer || !referrer || attr.referrer === referrer)) {
    currentAttributionValue = attr
    currentAttributionLocation = locationUrl()
    read = true
  }
  if (key) {
    putAttribution(key, undefined)
  }
  return read
}

export function currentAttribution(): Partial<Attribution> {
  // if location is changed without a reload it is worth reloading the attribution
  if (locationUrl() !== currentAttributionLocation && !readAttribution(true)) {
    return {}
  }
  return { ...currentAttributionValue?.attribution }
}

function isAnchorElement(et: EventTarget | null): et is HTMLAnchorElement {
  if (!et) {
    return false
  }

  // Fast path for native anchor elements with href
  if (et instanceof HTMLAnchorElement && et.hasAttribute("href")) {
    return true
  }
  // Fallback for elements that may be anchors in different contexts (e.g., iframes)
  if (isHTMLElement(et)) {
    return et.tagName === "A" && et.hasAttribute("href")
  }
  return false
}

function isHTMLElement(target: EventTarget | null): target is HTMLElement {
  if (!target) {
    return false
  }
  const targetEl = target as Node
  return (
    targetEl instanceof HTMLElement ||
    // support cases where Nosto is included in a different iframe
    (targetEl &&
      typeof targetEl === "object" &&
      targetEl.nodeType === Node.ELEMENT_NODE &&
      typeof targetEl.nodeName === "string")
  )
}

async function recordProductAttribution(productId: string, ref: string) {
  try {
    await createAttribution().recordAttribution({ type: VIEW_PRODUCT, target: productId, ref }).done()
  } catch (err) {
    logger.warn("Failed to record attribution for data-nosto-product", err)
  }
}

function saveAttribution(event: Event) {
  if (!isHTMLElement(event.target)) {
    return
  }

  const targetElement = event.target.closest<HTMLElement>(`[${dataRef}]`)
  if (!targetElement) {
    return
  }

  const productElement = event.target.closest<HTMLElement>(`:not(a)[data-nosto-product]`)
  if (event.type === "click" && productElement && targetElement.contains(productElement)) {
    const productId = productElement.dataset.nostoProduct
    const { ref } = parseJSON<SerializedAttribution>(targetElement.dataset.nostoRef!)
    if (productId && ref) {
      recordProductAttribution(productId, ref)
      return
    }
  }

  const anchorElement = event.composedPath().find(isAnchorElement)
  if (
    !anchorElement ||
    (typeof targetElement.nostoAttributionCheck === "function" && !targetElement.nostoAttributionCheck(anchorElement))
  ) {
    return
  }
  const { href } = anchorElement
  if (href.startsWith("javascript:")) {
    // skip attribution handling for javascript links
    return
  }

  const { linkAttributions, ...campaignAttribution } = parseJSON<SerializedAttribution>(targetElement.dataset.nostoRef!)
  const attribution = { ...campaignAttribution, ...(linkAttributions || {})[href] }
  const productId = anchorElement.dataset.nostoProduct
  if (productId) {
    putAttribution(productIdToKey(productId), attribution)
  }
  putAttribution(href, attribution)
}

type Check = (e: HTMLElement) => boolean

export function wrapWithAttribution(
  element: HTMLElement,
  campaignAttribution: Attribution,
  linkAttributions?: PerLinkAttributions,
  check?: Check
) {
  if (!isHTMLElement(element)) {
    return
  }
  if (!element.nostoAttributionCheck) {
    if (check) {
      // put a function to check if we need to save the attribution
      element.nostoAttributionCheck = check
    } else {
      // mark element instance that it needs to handle attribution to prevent assigning multiple event listeners
      element.nostoAttributionCheck = true
    }
    element.addEventListener("click", saveAttribution)
    element.addEventListener("contextmenu", saveAttribution)
  }
  const combined = linkAttributions ? { ...campaignAttribution, linkAttributions } : campaignAttribution
  if (Object.keys(combined).length) {
    element.setAttribute(dataRef, JSON.stringify(combined))
  }
}
